import React, { useEffect, useState } from 'react'
import PropTypes from 'prop-types'

import i18n from '../../../../../../i18n'
import AdSuggestions from './AdSuggestions'
import { SecondaryButton, TransparentBgButton } from 'components/Shared/Button'
import PortalModal from 'components/Shared/PortalModal'
import { CancelIcon, ArrowDownThreeIcon } from 'images'
import { Trans, useTranslation } from 'react-i18next'
import { isEqual } from 'lodash'
import { useCampaignSetupDispatch, useCampaignSetup } from 'components/Recommendations/Campaign/Context/CampaignSetupContext'
import { expandReelVariations } from 'components/Recommendations/Campaign/Context/disabledVariations'
import LoadingProgressBar from 'components/Shared/LoadingProgressBar'

const AdsSummaryItem = ({ title, value, adSuggestions, loading }) => {
  const { t } = useTranslation()
  const [isModalOpen, setModalOpen] = useState(false)
  const [currentTab, setCurrentTab] = useState(0)
  const { setup, disabledVariations } = useCampaignSetup()
  const { updateDisabledAdVariations } = useCampaignSetupDispatch()
  const [disabledVariationsPromptOpen, setDisabledVariationsPromptOpen] = useState(false)

  const [disabledVariationsLoading, setDisabledVariationsLoading] = useState(false)
  const [disabledVariationsError, setDisabledVariationsError] = useState(null)
  const [internalDisabledVariations, setInternalDisabledVariations] = useState(disabledVariations)

  useEffect(() => {
    if (!disabledVariationsLoading && !disabledVariationsError) {
      setDisabledVariationsPromptOpen(false)
    }
  }, [disabledVariationsLoading, disabledVariationsError])

  const syncDisabledVariations = () => {
    setModalOpen(false)
    setCurrentTab(0)
    setDisabledVariationsLoading(true)
    setDisabledVariationsError(false)
    const expandedVariations = expandReelVariations(internalDisabledVariations, setup.creatives)
    updateDisabledAdVariations(expandedVariations, ({ success }) => {
      setDisabledVariationsLoading(false)
      if (success) {
        setInternalDisabledVariations(expandedVariations)
      } else {
        setDisabledVariationsError(true)
      }
    })
  }

  const onModalClose = () => {
    if (!isEqual(disabledVariations, internalDisabledVariations)) {
      setDisabledVariationsPromptOpen(true)
    } else {
      setModalOpen(false)
      setCurrentTab(0)
    }
  }

  const leadSupportArticleUrl =
    i18n.language === 'de'
      ? 'https://support.future-demand.com/de/wie-kann-ich-anzeigen-mit-einem-lead-formular-erstellen'
      : 'https://support.future-demand.com/how-to-create-ads-with-lead-forms'

  return (
    <div className="card card-no-padding-mobile rounded flex-row">
      <div className="d-flex w-100">
        <div>
          <div className="summary_item_icon">
            <i className="icon-campaign" />
          </div>
        </div>
        <div className="d-flex justify-content-between flex-wrap gap-3 w-100">
          <p className="summary_item_content">
            {loading ? <LoadingProgressBar visible className="ads-preview-loader" /> : <strong>{value}</strong>}
            <span>{title}</span>
          </p>
          <div className="summary_item_button">
            <SecondaryButton
              text={t('Recommendations.summary.previewAds')}
              color="navy"
              size="small"
              onClick={() => setModalOpen(true)}
              disabled={loading}
            />
          </div>
        </div>
      </div>

      <PortalModal
        isOpen={isModalOpen && !disabledVariationsPromptOpen}
        className="full-height"
        onClickOutside={onModalClose}
      >
        <div className="summary_modal" style={{ height: '85vh' }}>
          <div className="summary_modal_disclaimer">
            <div>
              <h4>{t('Recommendations.summary.previewDisclaimer.title')}</h4>
              <div>{t('Recommendations.summary.previewDisclaimer.description1')}</div>
              <div>
                <Trans
                  i18nKey="Recommendations.summary.previewDisclaimer.description2"
                  components={{
                    a: (
                      <a
                        href={leadSupportArticleUrl}
                        className="infobox-tooltip__link"
                        rel="noopener noreferrer"
                        target="_blank"
                      >
                        support article
                      </a>
                    ),
                  }}
                />
              </div>
              <div className="mt-3">{t('Recommendations.summary.previewDisclaimer.description3')}</div>
            </div>
            <div>
              <button type="button" onClick={onModalClose} className="summary_close-btn_wrapper">
                <CancelIcon width={14} className="summary_close-btn_icon" />
              </button>
            </div>
          </div>
          <div className="summary_modal_down-arrow">
            <ArrowDownThreeIcon
              width={20}
              onClick={() => {
                setCurrentTab(0)
                window.location = '#ad-suggestions'
              }}
              className="c-hover"
            ></ArrowDownThreeIcon>
          </div>
          <div className="summary_modal_content" id="ad-suggestions">
            <AdSuggestions
              adSuggestionsRegular={adSuggestions.regular}
              adSuggestionsReels={adSuggestions.reels}
              currentTab={currentTab}
              setCurrentTab={(tab) => {
                setCurrentTab(tab)
                window.location = '#ad-suggestions'
              }}
              disabledVariations={internalDisabledVariations}
              onDisabledVariationsChange={setInternalDisabledVariations}
            />
          </div>
        </div>
      </PortalModal>

      <PortalModal isOpen={disabledVariationsPromptOpen} onClickOutside={() => setDisabledVariationsPromptOpen(false)}>
        <div className="content compact text-center" style={{ maxWidth: '420px', minHeight: '178px' }}>
          {!disabledVariationsLoading && !disabledVariationsError ? (
            <>
              <h1 className="autofill_warning-header">{t('Recommendations.summary.confirmChanges')}</h1>
              <p className="autofill_warning-msg">{t('Recommendations.summary.confirmChangesDescription')}</p>
              <div className="footer">
                <TransparentBgButton
                  text={t('common.cancel')}
                  onClick={() => setDisabledVariationsPromptOpen(false)}
                  color="navy"
                />
                <SecondaryButton
                  color="orange"
                  text={t('common.confirm')}
                  onClick={syncDisabledVariations}
                  type="button"
                  disabledTitle=""
                />
              </div>
            </>
          ) : disabledVariationsLoading ? (
            <LoadingProgressBar visible className="autofill_falling-dots" />
          ) : (
            <>
              {/* embedded the svg directly to git rid of layout shifts */}
              {/* warning-circle.svg */}
              <svg
                width="46"
                height="46"
                className="mt-4 mb-1"
                viewBox="0 0 56 56"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  d="M56 28C56 35.4261 53.05 42.548 47.799 47.799C42.548 53.05 35.4261 56 28 56C20.5739 56 13.452 53.05 8.20101 47.799C2.94999 42.548 0 35.4261 0 28C0 20.5739 2.94999 13.452 8.20101 8.20101C13.452 2.94999 20.5739 0 28 0C35.4261 0 42.548 2.94999 47.799 8.20101C53.05 13.452 56 20.5739 56 28ZM28 14C27.5576 14.0003 27.1202 14.0932 26.7159 14.2727C26.3116 14.4523 25.9495 14.7146 25.6527 15.0427C25.356 15.3708 25.1312 15.7574 24.993 16.1776C24.8547 16.5978 24.806 17.0423 24.85 17.4825L26.075 29.757C26.1162 30.2392 26.3368 30.6884 26.6933 31.0157C27.0497 31.3431 27.516 31.5247 28 31.5247C28.484 31.5247 28.9503 31.3431 29.3067 31.0157C29.6632 30.6884 29.8838 30.2392 29.925 29.757L31.15 17.4825C31.194 17.0423 31.1453 16.5978 31.007 16.1776C30.8688 15.7574 30.644 15.3708 30.3473 15.0427C30.0505 14.7146 29.6884 14.4523 29.2841 14.2727C28.8798 14.0932 28.4424 14.0003 28 14ZM28.007 35C27.0787 35 26.1885 35.3688 25.5321 36.0251C24.8757 36.6815 24.507 37.5717 24.507 38.5C24.507 39.4283 24.8757 40.3185 25.5321 40.9749C26.1885 41.6312 27.0787 42 28.007 42C28.9353 42 29.8255 41.6312 30.4819 40.9749C31.1383 40.3185 31.507 39.4283 31.507 38.5C31.507 37.5717 31.1383 36.6815 30.4819 36.0251C29.8255 35.3688 28.9353 35 28.007 35Z"
                  fill="#1F5274"
                />
              </svg>

              <div className="text-center mt-3">
                <h1 className="autofill_warning-header">{t('common.whoops')}!</h1>
                <p className="autofill_warning-msg mb-4">{t('common.requestError')}</p>
              </div>
              <SecondaryButton
                color="navy"
                disabled={disabledVariationsLoading}
                onClick={syncDisabledVariations}
                type="button"
                text={t('common.tryAgain')}
              />
            </>
          )}
        </div>
      </PortalModal>
    </div>
  )
}

AdsSummaryItem.propTypes = {
  title: PropTypes.string.isRequired,
  value: PropTypes.string.isRequired,
  adSuggestions: PropTypes.object,
  loading: PropTypes.bool,
}

export default AdsSummaryItem
